/** The « Regrouper par » control of a grouped stock page: see `grouping.ts` for what it edits. */

import { useTranslation } from "react-i18next";
import { Button, Select } from "@hardauto/core-ui";

import type { StockGroupLevel } from "../api";
import { NAMESPACE } from "../i18n";
import { useLevelName, type Grouping } from "./grouping";

/**
 * « Regrouper par » — the levels in use, in order, each movable and removable, and the
 * ones still unused to add. At least one level always stays: a page grouped by nothing
 * would be the flat list, which is another page.
 */
export function GroupingPicker({
  grouping,
  allowed,
}: {
  grouping: Grouping;
  allowed: readonly StockGroupLevel[];
}) {
  const { t } = useTranslation(NAMESPACE);
  const name = useLevelName();
  const { levels, change } = grouping;
  const unused = allowed.filter((level) => !levels.includes(level));

  const move = (index: number, by: -1 | 1) => {
    const next = [...levels];
    const target = index + by;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target]!, next[index]!];
    change(next);
  };

  return (
    <div className="hardauto-grouping" role="group" aria-label={t("grouping.label")}>
      <span className="hardauto-grouping__title">{t("grouping.label")}</span>
      <ol className="hardauto-grouping__levels">
        {levels.map((level, index) => (
          <li key={level} className="hardauto-grouping__level">
            <span className="hardauto-grouping__name">
              {index + 1}. {name(level)}
            </span>
            <button
              type="button"
              className="hardauto-grouping__button"
              aria-label={t("grouping.earlier", { level: name(level) })}
              disabled={index === 0}
              onClick={() => move(index, -1)}
            >
              ←
            </button>
            <button
              type="button"
              className="hardauto-grouping__button"
              aria-label={t("grouping.later", { level: name(level) })}
              disabled={index === levels.length - 1}
              onClick={() => move(index, 1)}
            >
              →
            </button>
            <button
              type="button"
              className="hardauto-grouping__button"
              aria-label={t("grouping.remove", { level: name(level) })}
              disabled={levels.length === 1}
              onClick={() => change(levels.filter((other) => other !== level))}
            >
              ×
            </button>
          </li>
        ))}
      </ol>
      {unused.length > 0 ? (
        <Select
          aria-label={t("grouping.add")}
          value=""
          onChange={(event) => {
            const chosen = unused.find((level) => level === event.target.value);
            if (chosen) change([...levels, chosen]);
          }}
        >
          <option value="">{t("grouping.add")}</option>
          {unused.map((level) => (
            <option key={level} value={level}>
              {name(level)}
            </option>
          ))}
        </Select>
      ) : null}
      {grouping.isDefault ? null : (
        <Button variant="ghost" size="sm" onClick={grouping.reset}>
          {t("grouping.reset")}
        </Button>
      )}
    </div>
  );
}
